import React from "react";
import "../index.css";

const LOGO = "/img/N_logo_pink_background.png";

function LegalNav() {
  return (
    <nav className="legal-page-nav">
      <a href="/" className="legal-page-nav-logo">
        <img src={LOGO} alt="Ninoa" />
        <span>Ninoa</span>
      </a>
      <div className="legal-page-nav-links">
        <a href="/terms">Terms</a>
        <a href="/privacy">Privacy</a>
        <a href="/support">Support</a>
      </div>
    </nav>
  );
}

function LegalFooter() {
  return (
    <footer className="legal-page-footer">
      <div className="legal-page-footer-inner">
        <span>&copy; 2026 Ninoa. All rights reserved.</span>
        <a href="mailto:support@ninoa.space">support@ninoa.space</a>
      </div>
    </footer>
  );
}

/* ═══════ TERMS ═══════ */
export function TermsPage() {
  return (
    <div className="legal-page">
      <LegalNav />
      <div className="legal-page-content">
        <div className="legal-page-header">
          <span className="legal-page-badge">LEGAL</span>
          <h1>Terms & Conditions</h1>
          <p className="legal-page-updated">Last updated: June 11, 2026</p>
        </div>

        <div className="legal-page-body">
          <section>
            <h2>1. Introduction</h2>
            <p>These Terms & Conditions ("Terms") govern your use of the Ninoa mobile application and related services ("Ninoa" or "the Service"), operated by Mariam Giorgobiani (sole proprietor / autónomo), based in Barcelona, Spain.</p>
            <p>By creating an account or using Ninoa, you agree to these Terms. If you do not agree, do not use the Service.</p>
          </section>

          <section>
            <h2>2. Age Requirement</h2>
            <p>You must be at least <strong>16 years old</strong> (in the EU) or <strong>13 years old</strong> (in the United States) to use Ninoa. By using the app, you confirm that you meet the applicable age requirement. If you are under 18, you confirm that you have the consent of a parent or guardian.</p>
          </section>

          <section>
            <h2>3. What Ninoa Provides</h2>
            <p>Ninoa helps you track symptoms, lifestyle factors, and treatment progress for chronic skin conditions. Ninoa provides wellness insights and pattern analysis based on the data you self-report.</p>

            <div className="legal-page-disclaimer">
              <h3>Medical Disclaimer</h3>
              <p><strong>Ninoa is a wellness and self-tracking tool. It is NOT a medical device.</strong></p>
              <ul>
                <li>Ninoa does <strong>not</strong> diagnose, treat, cure, or prevent any disease or medical condition.</li>
                <li>Predictions, insights, and pattern analyses are based entirely on your self-reported data and should not be considered medical advice.</li>
                <li><strong>Always consult a qualified healthcare professional</strong> before making any decisions about your health or treatment.</li>
                <li>Do not make medical decisions based solely on information provided by Ninoa.</li>
                <li>In case of a medical emergency, contact your local emergency services immediately.</li>
              </ul>
            </div>
          </section>

          <section>
            <h2>4. Subscription & Payments</h2>
            <p>Ninoa offers the following <strong>auto-renewable subscriptions</strong> through the Apple App Store and Google Play Store:</p>
            <ul>
              <li><strong>Ninoa Basic</strong> — €6.99/month or €74.99/year</li>
              <li><strong>Ninoa Premium</strong> — €10.99/month or €119.99/year (includes a 7-day free trial for new users)</li>
            </ul>

            <h3>Subscription Details</h3>
            <ul>
              <li>Payment will be charged to your <strong>Apple ID or Google Play account</strong> at the confirmation of purchase.</li>
              <li>Your subscription <strong>automatically renews</strong> unless auto-renew is turned off at least <strong>24 hours before</strong> the end of the current period.</li>
              <li>Your account will be charged for renewal <strong>within 24 hours prior to the end of the current period</strong>, at the price of the chosen package.</li>
              <li>You can manage your subscription and turn off auto-renewal at any time:
                <ul>
                  <li><strong>iOS:</strong> Settings → [your name] → Subscriptions → Ninoa</li>
                  <li><strong>Android:</strong> Google Play app → menu → Subscriptions → Ninoa</li>
                </ul>
              </li>
              <li>Any unused portion of a <strong>free trial period</strong>, if offered, will be forfeited when you purchase a subscription before the trial period ends.</li>
              <li>Refunds are handled by Apple or Google according to their respective refund policies. Contact Apple or Google support directly for refund requests.</li>
              <li>Ninoa reserves the right to change subscription pricing with reasonable notice to existing subscribers.</li>
            </ul>

            <p><strong>Terms of Use (EULA):</strong> <a href="/terms">https://ninoa.space/terms</a></p>
            <p><strong>Privacy Policy:</strong> <a href="/privacy">https://ninoa.space/privacy</a></p>
          </section>

          <section>
            <h2>5. User Responsibilities</h2>
            <ul>
              <li>Provide accurate information when creating your account and logging data.</li>
              <li>Use the app in compliance with all applicable laws and regulations.</li>
              <li>Keep your login credentials secure and do not share your account.</li>
              <li>Do not attempt to reverse-engineer, modify, or tamper with the app.</li>
              <li>Do not use Ninoa for any unlawful or harmful purpose.</li>
            </ul>
          </section>

          <section>
            <h2>6. Data Ownership</h2>
            <p>You own your data. You may export or delete it at any time. By using Ninoa, you grant us a limited license to process your data solely for the purpose of providing and improving the Service, as described in our <a href="/privacy">Privacy Policy</a>.</p>
          </section>

          <section>
            <h2>7. Intellectual Property</h2>
            <p>All content, design, graphics, logos, icons, code, and other materials within Ninoa are owned by Ninoa (Mariam Giorgobiani) or its licensors and are protected by applicable intellectual property laws.</p>
            <ul>
              <li>You may not copy, modify, distribute, sell, or create derivative works based on Ninoa's content without prior written permission.</li>
              <li>The Ninoa name and logo are proprietary marks and may not be used without authorization.</li>
              <li>User-generated content (your logs, photos, notes) remains your property.</li>
            </ul>
          </section>

          <section>
            <h2>8. Availability</h2>
            <p>We strive to keep Ninoa available at all times but do not guarantee uninterrupted access. We may update, modify, or temporarily suspend the app for improvements, maintenance, or security purposes. We will make reasonable efforts to notify users of planned downtime.</p>
          </section>

          <section>
            <h2>9. Liability</h2>
            <ul>
              <li>Ninoa is provided "as is" and "as available" without warranties of any kind, either express or implied.</li>
              <li>Ninoa does not provide medical advice — see the Medical Disclaimer above.</li>
              <li>Incorrect or incomplete self-reported data may affect the quality of insights.</li>
              <li>Temporary downtime or service interruptions may occur.</li>
              <li>To the fullest extent permitted by law, Ninoa and its operator shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service.</li>
            </ul>
          </section>

          <section>
            <h2>10. Termination</h2>
            <ul>
              <li>You may delete your account at any time through the app. Upon deletion, your personal data will be permanently removed within 30 days, as described in our <a href="/privacy">Privacy Policy</a>.</li>
              <li>We may suspend or terminate accounts that violate these Terms, engage in fraudulent activity, or misuse the Service.</li>
              <li>If we terminate your account, we will notify you via the email associated with your account, unless the termination is due to illegal activity.</li>
            </ul>
          </section>

          <section>
            <h2>11. Governing Law & Disputes</h2>
            <p>These Terms are governed by and construed in accordance with the laws of Spain. Any disputes arising from or related to these Terms or your use of Ninoa shall be resolved in the courts of Barcelona, Spain.</p>
            <p>If you are an EU consumer, you may also be entitled to use the European Commission's Online Dispute Resolution platform at <a href="https://ec.europa.eu/odr" target="_blank" rel="noopener noreferrer">https://ec.europa.eu/odr</a>.</p>
          </section>

          <section>
            <h2>12. Changes to These Terms</h2>
            <p>We may update these Terms from time to time. When we make changes, we will update the "Last updated" date at the top of this page and notify users via email or in-app notice for significant changes. Continued use of Ninoa after changes constitutes acceptance of the updated Terms.</p>
          </section>

          <section>
            <h2>13. Contact</h2>
            <p>For questions about these Terms, contact us at: <a href="mailto:support@ninoa.space">support@ninoa.space</a></p>
            <p>Ninoa by Mariam Giorgobiani<br />Barcelona, Spain</p>
          </section>
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}

/* ═══════ PRIVACY ═══════ */
export function PrivacyPage() {
  return (
    <div className="legal-page">
      <LegalNav />
      <div className="legal-page-content">
        <div className="legal-page-header">
          <span className="legal-page-badge">LEGAL</span>
          <h1>Privacy Policy</h1>
          <p className="legal-page-updated">Last updated: April 27, 2026</p>
        </div>

        <div className="legal-page-body">
          <p className="legal-page-intro">Your privacy matters deeply to us. Ninoa was created by someone who lives with a chronic condition and knows how sensitive health data can be. We collect only the minimum information necessary for the app to function — nothing more.</p>

          <section>
            <h2>1. Data Controller</h2>
            <p>Ninoa is operated by Mariam Giorgobiani (sole proprietor / autónomo), based in Barcelona, Spain. For any privacy inquiries, contact <a href="mailto:support@ninoa.space">support@ninoa.space</a>.</p>
          </section>

          <section>
            <h2>2. Personal Data We Collect</h2>
            <p>Ninoa collects only the information you choose to provide:</p>
            <ul>
              <li>Email address (for account creation and authentication)</li>
              <li>Basic profile information (optional)</li>
              <li>Daily logs (symptoms, mood, sleep, routines, flare-ups, stress levels, triggers, treatments, product use, notes)</li>
              <li>Photos (optional — deletable anytime)</li>
              <li>Timestamps & metadata</li>
              <li>Technical info (device type, app version)</li>
            </ul>
          </section>

          <section>
            <h2>3. Legal Basis for Processing (GDPR Article 6)</h2>
            <p>We process your personal data based on the following legal grounds:</p>
            <ul>
              <li><strong>Consent</strong> — You provide consent when you create an account and agree to this Privacy Policy.</li>
              <li><strong>Contract performance</strong> — Processing is necessary to provide you with the app's features and services you have requested.</li>
              <li><strong>Legitimate interest</strong> — We may process anonymized or aggregated data to improve Ninoa's service, provided this does not override your rights.</li>
            </ul>
          </section>

          <section>
            <h2>4. How We Use Your Data</h2>
            <ul>
              <li>Core app functionality (tracking, insights, personalization)</li>
              <li>Pattern insights and trend analysis based on your self-reported data</li>
              <li>App improvement and bug fixing</li>
              <li>User support and communication</li>
            </ul>
          </section>

          <section>
            <h2>5. Anonymized & Aggregated Data</h2>
            <p>Ninoa may create anonymized, aggregated datasets from user activity. This data:</p>
            <ul>
              <li>Cannot identify you personally — all personal identifiers (name, email, device IDs, IP addresses) are permanently removed</li>
              <li>Does not include photos</li>
              <li>Is used to improve app features, predictions, and user experience</li>
            </ul>
          </section>

          <section>
            <h2>6. De-identified Data for Research</h2>
            <p>With your <strong>separate, explicit consent</strong> (provided during onboarding or in Settings &gt; Privacy &amp; Data), we may share de-identified, aggregated health data with:</p>
            <ul>
              <li>Medical research institutions and universities</li>
              <li>Pharmaceutical companies conducting real-world evidence studies</li>
              <li>Healthcare organizations working to improve treatment outcomes</li>
            </ul>
            <p><strong>"De-identified" means:</strong> all personal identifiers (name, email, device IDs, IP addresses) are permanently removed before any data is shared. The shared data includes only anonymized patterns such as symptom trends, trigger correlations, treatment outcomes, and demographic breakdowns (age range, country, condition type).</p>
            <p>This consent is:</p>
            <ul>
              <li><strong>Separate</strong> from your agreement to use Ninoa — you do not need to consent to use the app</li>
              <li><strong>Voluntary</strong> — declining does not affect your access to any features</li>
              <li><strong>Revocable</strong> — you can withdraw consent at any time in the app (Settings &gt; Privacy &amp; Data)</li>
              <li><strong>Granular</strong> — applies only to research use, not advertising or marketing</li>
            </ul>
            <p>We comply with GDPR Article 9 requirements for processing special category (health) data, which requires explicit, informed, and freely given consent.</p>
          </section>

          <section>
            <h2>7. Data Storage & Location</h2>
            <p>All your data is stored within the European Union:</p>
            <ul>
              <li>AWS RDS PostgreSQL — EU North (Stockholm, Sweden)</li>
              <li>AWS ECS Fargate (Backend) — EU North (Stockholm, Sweden)</li>
              <li>AWS S3 (Images) — EU North (Stockholm, Sweden)</li>
              <li>AWS Cognito (Authentication) — EU North (Stockholm, Sweden)</li>
              <li>All connections encrypted via HTTPS/TLS</li>
            </ul>
            <div className="legal-page-highlight">
              <strong>No data is transferred outside the EU/EEA.</strong> All infrastructure is hosted in the AWS eu-north-1 region (Stockholm, Sweden).
            </div>
          </section>

          <section>
            <h2>7b. AI-Powered Features</h2>
            <p>
              Ninoa includes an optional AI Skin Scanner that analyzes a photo of your skin
              to produce gentle visual observations and track changes over time. When you
              use the scanner:
            </p>
            <ul>
              <li>
                <strong>What is sent:</strong> the photo you choose to scan, the body area
                label you tag, and (for side-by-side comparison) any prior scan observations
                of the same area.
              </li>
              <li>
                <strong>Where it goes:</strong> the photo is transmitted over encrypted
                HTTPS to <strong>Anthropic</strong> (Claude vision API) for real-time
                analysis. Anthropic does <strong>not retain</strong> the photo or response
                after processing, per their API terms.
              </li>
              <li>
                <strong>What we store:</strong> the photo itself is stored privately in
                your account on AWS S3 (EU-North, Stockholm) with a signed-URL access
                layer. The AI's text observations are stored alongside it in our database.
                Both are deleted when you delete your account.
              </li>
              <li>
                <strong>Not a diagnosis:</strong> the AI's output is a visual observation
                only — not a medical diagnosis. The system prompt instructs the AI to
                avoid clinical claims and to recommend a qualified dermatologist for any
                actual assessment.
              </li>
            </ul>
          </section>

          <section>
            <h2>8. Data Retention</h2>
            <ul>
              <li><strong>Active accounts:</strong> Your personal data is retained for as long as your account remains active.</li>
              <li><strong>Deleted accounts:</strong> All personal data is permanently deleted within 30 days of account deletion.</li>
              <li><strong>Anonymized data:</strong> Aggregated, anonymized data that cannot identify you may be retained indefinitely for research and service improvement purposes.</li>
              <li><strong>Backups:</strong> Personal data in backups is purged within 30 days following account deletion.</li>
            </ul>
          </section>

          <section>
            <h2>9. Your Privacy Rights</h2>
            <p>Under the GDPR and applicable data protection laws, you have the following rights:</p>
            <ul>
              <li><strong>Right to access</strong> — Request a copy of the personal data we hold about you.</li>
              <li><strong>Right to rectification</strong> — Request correction of inaccurate or incomplete data.</li>
              <li><strong>Right to erasure ("right to be forgotten")</strong> — Request deletion of your personal data.</li>
              <li><strong>Right to data portability</strong> — Request your data in a structured, commonly used, machine-readable format.</li>
              <li><strong>Right to restrict processing</strong> — Request that we limit how we use your data.</li>
              <li><strong>Right to object</strong> — Object to processing based on legitimate interest.</li>
              <li><strong>Right to withdraw consent</strong> — Withdraw your consent at any time (this does not affect the lawfulness of processing before withdrawal).</li>
            </ul>
            <p>To exercise any of these rights, contact us at <a href="mailto:support@ninoa.space">support@ninoa.space</a>. We will respond within 30 days.</p>
            <p>You also have the <strong>right to lodge a complaint</strong> with the Spanish Data Protection Authority (Agencia Española de Protección de Datos — AEPD) at <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">www.aepd.es</a>.</p>
          </section>

          <section>
            <h2>10. CCPA Notice (California Residents)</h2>
            <p>If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with additional rights:</p>
            <ul>
              <li><strong>Right to know</strong> — You can request details about what personal information we collect, the sources, the purposes, and with whom we share it.</li>
              <li><strong>Right to delete</strong> — You can request deletion of your personal information.</li>
              <li><strong>Right to opt-out of sale</strong> — You can opt out of the sale of your personal information.</li>
              <li><strong>Right to non-discrimination</strong> — We will not discriminate against you for exercising your CCPA rights.</li>
            </ul>
            <div className="legal-page-highlight">
              <strong>Ninoa does not sell your personal information.</strong> With your explicit consent, we may share de-identified, aggregated data (which cannot identify you) with research partners. You can opt out of this at any time in the app's Privacy &amp; Data settings. We never share data that could identify you personally.
            </div>
            <p>To exercise your CCPA rights, contact us at <a href="mailto:support@ninoa.space">support@ninoa.space</a>.</p>
          </section>

          <section>
            <h2>11. Third-Party Services</h2>
            <p>Ninoa uses the following third-party services to operate:</p>
            <ul>
              <li><strong>Amazon Web Services (AWS)</strong> — Hosting, storage, database, and authentication. All data remains in the EU (eu-north-1, Stockholm, Sweden).</li>
              <li><strong>Anthropic (Claude API)</strong> — AI vision analysis for the optional Skin Scanner feature. Photos are sent for real-time processing; not retained by Anthropic per their API terms. See Section 7b.</li>
              <li><strong>RevenueCat</strong> — Subscription management. Receives only anonymized in-app purchase events (no health data).</li>
              <li><strong>Stripe</strong> — Payment processing for any subscribers on the legacy web-checkout pricing plan. Stripe processes payment data directly; we do not store card details.</li>
              <li><strong>Expo</strong> — App distribution and over-the-air updates.</li>
              <li><strong>Sentry</strong> — Error monitoring and crash reporting. Receives technical diagnostics (stack traces, device model, OS version, IP). No health data is sent.</li>
              <li><strong>Mixpanel</strong> — Anonymous usage analytics. Receives screen-flow events and device info. No health data is sent.</li>
              <li><strong>OpenWeatherMap</strong> — Weather data based on IP-derived location only. Your precise location is not sent or stored.</li>
            </ul>
            <p>We do not share your personal data with advertisers or data brokers.</p>
          </section>

          <section>
            <h2>12. Cookies</h2>
            <p>The Ninoa website may use essential cookies strictly necessary for functionality (e.g., session management). We do <strong>not</strong> use advertising cookies, tracking cookies, or third-party marketing cookies.</p>
          </section>

          <section>
            <h2>13. Security Measures</h2>
            <ul>
              <li>HTTPS/TLS encryption for all data in transit</li>
              <li>JWT-based authentication</li>
              <li>Role-based access control</li>
              <li>Secure password hashing</li>
              <li>System monitoring and logging</li>
              <li>Regular security reviews</li>
            </ul>
          </section>

          <section>
            <h2>14. Children's Privacy</h2>
            <p>Ninoa is not intended for children under 16 years old (as required by the GDPR) or under 13 years old (as required by COPPA in the United States). We do not knowingly collect personal data from children. If we become aware that we have collected data from a child, we will delete it promptly. If you believe a child has provided us with personal data, please contact us at <a href="mailto:support@ninoa.space">support@ninoa.space</a>.</p>
          </section>

          <section>
            <h2>15. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. When we make changes, we will update the "Last updated" date at the top of this page and notify users via email or in-app notice for significant changes. We encourage you to review this policy periodically.</p>
          </section>

          <section>
            <h2>Contact</h2>
            <p>If you have any questions about this Privacy Policy or your data, contact us at: <a href="mailto:support@ninoa.space">support@ninoa.space</a></p>
            <p>Ninoa by Mariam Giorgobiani<br />Barcelona, Spain</p>
          </section>
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}

/* ═══════ SUPPORT ═══════ */
export function SupportPage() {
  return (
    <div className="legal-page">
      <LegalNav />
      <div className="legal-page-content">
        <div className="legal-page-header">
          <span className="legal-page-badge">SUPPORT</span>
          <h1>How can we help?</h1>
          <p className="legal-page-updated">We're here for you</p>
        </div>

        <div className="legal-page-body">
          <div className="legal-page-support-card">
            <h2>Get in touch</h2>
            <p>If you have any questions, feedback, or need help with your account, we'd love to hear from you.</p>
            <a href="mailto:support@ninoa.space" className="legal-page-email-btn">
              support@ninoa.space
            </a>
            <p className="legal-page-response">We aim to reply as quickly as possible.</p>
          </div>

          <section style={{ marginTop: 40 }}>
            <h2>Delete your Ninoa account and data</h2>
            <p>
              You can permanently delete your Ninoa account and all associated
              data at any time. Two ways to do this:
            </p>

            <h3>Option 1 — In the Ninoa app (fastest)</h3>
            <ol>
              <li>Open the Ninoa app (iOS or Android)</li>
              <li>Sign in if you aren't already</li>
              <li>Tap the <strong>Profile</strong> tab</li>
              <li>Scroll to the <strong>Settings</strong> card and tap <strong>Delete Account</strong></li>
              <li>Confirm by tapping <strong>Yes, Delete Account</strong></li>
              <li>Select a reason (optional) and tap <strong>Delete Account</strong></li>
            </ol>
            <p>
              Your account and all data are removed immediately. You'll be signed out automatically.
            </p>

            <h3>Option 2 — By email</h3>
            <p>
              If you can't access the app, send an account deletion request to
              {' '}<a href="mailto:support@ninoa.space?subject=Account%20Deletion%20Request">support@ninoa.space</a>
              {' '}from the email address registered with your Ninoa account. Include
              the words "Account Deletion Request" in the subject line.
              We'll process your request within 7 business days and confirm by reply email.
            </p>

            <h3>What gets deleted</h3>
            <ul>
              <li>Your profile (name, email, birthdate, condition, country, etc.)</li>
              <li>All daily logs, symptoms, mood ratings, food triggers, and notes</li>
              <li>All skin scan photos and AI-generated observations</li>
              <li>All medications, appointments, and reminders you've logged</li>
              <li>All challenge entries, doctor reports, and personal insights</li>
              <li>Your authentication record (AWS Cognito user account)</li>
              <li>All push notification tokens and in-app notifications</li>
            </ul>

            <h3>What is kept (anonymized)</h3>
            <ul>
              <li>
                <strong>Anonymized usage logs</strong> may be retained for product
                analytics. These have your user identifier removed and cannot be used
                to identify you personally.
              </li>
              <li>
                <strong>Community posts and comments</strong> remain visible to other
                users for community continuity, but are re-attributed to a deleted-user
                placeholder (no name, email, or other identifying information).
              </li>
              <li>
                <strong>Backups</strong> may contain your data for up to 30 days after
                deletion before they are also purged.
              </li>
            </ul>

            <h3>If you have an active subscription</h3>
            <p>
              If you're subscribed to Ninoa Premium or Basic, your subscription
              continues to be managed by the App Store or Google Play directly —
              deleting your Ninoa account does <strong>not</strong> automatically
              cancel your subscription. Cancel your subscription separately at:
            </p>
            <ul>
              <li><strong>iOS:</strong> Settings → [your name] → Subscriptions → Ninoa → Cancel</li>
              <li><strong>Android:</strong> Google Play app → menu → Subscriptions → Ninoa → Cancel</li>
            </ul>

            <h3>Questions?</h3>
            <p>
              Reach out at <a href="mailto:support@ninoa.space">support@ninoa.space</a>
              {' '}and we'll help you sort it out.
            </p>
          </section>
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}
